import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { toast } from '../../../components/Toast'
import { apiService } from '../../demands/services/api'
import { sistemasService } from '@/services/sistemasService'
// import { autorizadorService } from '../../../services/autorizador' // TEMPORÁRIO: Removido pois API foi desabilitada
import { usePermissions } from './usePermissions'
import { 
  useProcessos, 
  useCreateProcesso, 
  useUpdateProcesso, 
  useDeleteProcesso,
  type ProcessosFilters 
} from './useProcessos'
import { 
  useSetores, 
  useCreateSetor, 
  useCreateEquipe, 
  useUpdateSetor, 
  useDeleteSetor,
  type SetoresFilters 
} from './useSetores'
import {
  useSistemas,
  useCreateSistema,
  useUpdateSistema,
  useDeleteSistema,
  type SistemasFilters
} from './useSistemas'
import type { User, Setor, Processo, Autorizador, Sistema, CreateSistemaRequest } from '../../../models'
import type { CrudType, ViewMode } from '../components/CrudNavigation'

interface UseCrudLogicProps {
  currentUser: User | null
  currentType?: CrudType
  currentMode?: ViewMode
  searchQuery?: string
  selectedSetor?: number | null
}

type SistemaItemInput = {
  id?: number
  nome: string
  descricao?: string
}

export const useCrudLogic = ({ currentUser, currentType: externalCurrentType, currentMode: externalCurrentMode, searchQuery = '', selectedSetor = null }: UseCrudLogicProps) => {
  const permissions = usePermissions({ currentUser })

  // Determinar o tipo atual para otimizar as queries
  const currentType = externalCurrentType ?? 'processos'

  // Construir filtros apenas quando necessário para o tipo atual
  // Processos: apenas quando estiver na aba de processos
  const processosFilters: ProcessosFilters | undefined = useMemo(() => {
    if (currentType !== 'processos') return undefined

    const filters: ProcessosFilters = {}
    if (searchQuery && searchQuery.length >= 3) filters.buscar = searchQuery
    if (selectedSetor) filters.setor = selectedSetor
    return filters
  }, [currentType, searchQuery, selectedSetor])

  // Setores: apenas quando estiver na aba de setores
  // Inclui explicitamente equipe=false para filtrar no servidor
  const setoresFilters: SetoresFilters | undefined = currentType === 'setores' ? {
    ...(searchQuery && searchQuery.length >= 3 ? { buscar: searchQuery } : {}),
    equipe: false // Explicitamente filtrar apenas setores (não equipes)
  } : undefined

  // Equipes: apenas quando estiver na aba de equipes
  const equipesFilters: SetoresFilters | undefined = currentType === 'equipes' ? {
    ...(searchQuery && searchQuery.length >= 3 ? { buscar: searchQuery } : {}),
    equipe: true
  } : undefined

  // Sistemas: apenas quando estiver na aba de sistemas
  const sistemasFilters: SistemasFilters | undefined = currentType === 'sistemas' ? {
    ...(searchQuery && searchQuery.length >= 3 ? { buscar: searchQuery } : {}),
  } : undefined

  // Usar TanStack Query para processos e setores - com enabled condicional
  const { data: processosData = [], isLoading: isLoadingProcessosQuery } = useProcessos(
    processosFilters,
    { enabled: currentType === 'processos' }
  )
  const { data: setoresData = [], isLoading: isLoadingSetoresQuery } = useSetores(
    setoresFilters,
    { enabled: currentType === 'setores' }
  )
  const { data: equipesData = [], isLoading: isLoadingEquipesQuery } = useSetores(
    equipesFilters,
    { enabled: currentType === 'equipes' }
  )
  const { sistemas, loading: isLoadingSistemasQuery, refetch: refetchSistemas } = useSistemas(
    sistemasFilters
  )
  
  // Query para TODOS os setores (incluindo equipes) - usado no formulário de processos
  // Esta query é sempre habilitada para garantir que o formulário de processos tenha todos os setores disponíveis
  const { data: todosSetoresData = [], isLoading: isLoadingTodosSetores } = useSetores(
    undefined, // Sem filtros = retorna todos (setores e equipes)
    { enabled: true }
  )
  const createProcessoMutation = useCreateProcesso()
  const updateProcessoMutation = useUpdateProcesso()
  const deleteProcessoMutation = useDeleteProcesso()
  const createSetorMutation = useCreateSetor()
  const createEquipeMutation = useCreateEquipe()
  const updateSetorMutation = useUpdateSetor()
  const deleteSetorMutation = useDeleteSetor()
  const createSistemaMutation = useCreateSistema()
  const updateSistemaMutation = useUpdateSistema()
  const deleteSistemaMutation = useDeleteSistema()

  // Estados para dados
  const [usuarios, setUsuarios] = useState<User[]>([])
  const [setores, setSetores] = useState<Setor[]>([])
  const [todosSetores, setTodosSetores] = useState<Setor[]>([]) // Todos os setores + equipes para o formulário de processos
  const [processos, setProcessos] = useState<Processo[]>([])
  const [meusSetores, setMeusSetores] = useState<Setor[]>([])
  const [autorizadores, setAutorizadores] = useState<Autorizador[]>([])
  const [sistemasState, setSistemasState] = useState<Sistema[]>([])
  
  // Estados de carregamento
  const [isLoadingUsuarios, setIsLoadingUsuarios] = useState(true)
  const [isLoadingSetores, setIsLoadingSetores] = useState(true)
  const [isLoadingTodosSetoresState, setIsLoadingTodosSetoresState] = useState(true)
  const [isLoadingProcessos, setIsLoadingProcessos] = useState(true)
  const [isLoadingMeusSetores, setIsLoadingMeusSetores] = useState(true)
  const [isLoadingAutorizadores, setIsLoadingAutorizadores] = useState(true)
  const [isLoadingSistemas, setIsLoadingSistemas] = useState(true)

  // Sincronizar dados do TanStack Query com o estado local
  // Sempre atualizar quando os dados do TanStack Query mudarem
  useEffect(() => {
    if (currentType === 'processos') {
      setProcessos(processosData)
      setIsLoadingProcessos(isLoadingProcessosQuery)
    }
  }, [processosData, isLoadingProcessosQuery, currentType])
  
  useEffect(() => {
    // Atualizar estado de setores apenas quando estiver na aba correspondente
    if (currentType === 'setores') {
      setSetores(setoresData)
      setIsLoadingSetores(isLoadingSetoresQuery)
    } else if (currentType === 'equipes') {
      setSetores(equipesData)
      setIsLoadingSetores(isLoadingEquipesQuery)
    }
  }, [setoresData, equipesData, isLoadingSetoresQuery, isLoadingEquipesQuery, currentType])

  // Sincronizar TODOS os setores (para uso no formulário de processos)
  // Esta sincronização é independente da aba atual e sempre atualiza
  useEffect(() => {
    setTodosSetores(todosSetoresData)
    setIsLoadingTodosSetoresState(isLoadingTodosSetores)
  }, [todosSetoresData, isLoadingTodosSetores])

  // Sincronizar sistemas com o estado local
  // Sempre atualizar quando os dados do TanStack Query mudarem
  useEffect(() => {
    if (currentType === 'sistemas') {
      setSistemasState(sistemas)
      setIsLoadingSistemas(isLoadingSistemasQuery)
    }
  }, [sistemas, isLoadingSistemasQuery, currentType])
  
  // Estados da navegação (podem ser controlados externamente)
  // internalCurrentType foi removido pois currentType já é definido acima para otimizar as queries
  const [, setInternalCurrentType] = useState<CrudType>('processos')
  const [internalCurrentMode, setInternalCurrentMode] = useState<ViewMode>('list')
  
  // Usar valores externos se fornecidos, senão usar internos
  // NOTA: currentType já foi definido acima para otimizar as queries
  const currentMode = externalCurrentMode ?? internalCurrentMode
  
  // Estados de edição
  const [editingItem, setEditingItem] = useState<Processo | Setor | Autorizador | Sistema | null>(null)
  
  // Modal de gerenciamento de usuários
  const [manageUsersModal, setManageUsersModal] = useState<{
    isOpen: boolean
    setor: Setor | null
  }>({
    isOpen: false,
    setor: null,
  })

  const loadUsuarios = useCallback(async () => {
    try {
      setIsLoadingUsuarios(true)
      const data = await apiService.getUsuarios()
      setUsuarios(data)
    } catch (error) {
      console.error('Erro ao carregar usuários:', error)
      toast.error('Erro ao carregar lista de usuários')
    } finally {
      setIsLoadingUsuarios(false)
    }
  }, [])

  const loadSetores = useCallback(async () => {
    try {
      setIsLoadingSetores(true)
      const data = await apiService.getSetoresCompletos()
      setSetores(data)
    } catch (error) {
      console.error('Erro ao carregar setores:', error)
      toast.error('Erro ao carregar lista de setores')
    } finally {
      setIsLoadingSetores(false)
    }
  }, [])

  const loadProcessos = useCallback(async () => {
    try {
      setIsLoadingProcessos(true)
      // Todos os usuários (incluindo usuários normais) podem ver todos os processos
      // A restrição por setor só se aplica ao criar/editar, não à visualização
      const data = await apiService.getProcessosList()
      setProcessos(data)
    } catch (error) {
      console.error('Erro ao carregar processos:', error)
      toast.error('Erro ao carregar lista de processos')
    } finally {
      setIsLoadingProcessos(false)
    }
  }, [])

  const loadMeusSetores = useCallback(async (forceRefresh = false) => {
    try {
      setIsLoadingMeusSetores(true)
      const data = await apiService.getMeusSetores(forceRefresh)
      setMeusSetores(data)
    } catch (error) {
      console.error('Erro ao carregar meus setores:', error)
      toast.error('Erro ao carregar seus setores')
    } finally {
      setIsLoadingMeusSetores(false)
    }
  }, [])

  const loadAutorizadores = useCallback(async () => {
    try {
      setIsLoadingAutorizadores(true)
      // TEMPORÁRIO: Desabilitar chamada para API removida
      // const data = await autorizadorService.getAll()
      // setAutorizadores(data)
      setAutorizadores([]) // TEMPORÁRIO: Array vazio até reimplementação
    } catch (error) {
      console.error('Erro ao carregar autorizadores:', error)
      toast.error('Erro ao carregar lista de autorizadores')
    } finally {
      setIsLoadingAutorizadores(false)
    }
  }, [])

  const initialLoadSignatureRef = useRef<string | null>(null)

  const {
    canViewProcessos,
    canViewSetores,
    canViewEquipes,
    canViewAutorizadores,
    canCreateProcessos,
    canEditProcessos,
    canManageUsers,
    canCreateSetores,
    canCreateEquipes,
  } = permissions

  const permissionsSignature = [
    Number(canViewProcessos),
    Number(canViewSetores),
    Number(canViewEquipes),
    Number(canViewAutorizadores),
    Number(canCreateProcessos),
    Number(canEditProcessos),
    Number(canManageUsers),
    Number(canCreateSetores),
    Number(canCreateEquipes),
    permissions.isUserNormal ? `normal-${permissions.setorId ?? 'none'}` : `elev-${permissions.setorId ?? 'none'}`,
  ].join('|')

  // Carregar dados iniciais baseado nas permissões (evitando duplicações)
  useEffect(() => {
    if (initialLoadSignatureRef.current === permissionsSignature) {
      return
    }

    initialLoadSignatureRef.current = permissionsSignature

    if (canViewProcessos) {
      loadProcessos()
    }
    if (canViewSetores || canViewEquipes) {
      loadSetores()
    }
    if (canCreateProcessos || canEditProcessos) {
      loadMeusSetores()
    }
    if (canManageUsers || canCreateSetores || canCreateEquipes || canViewAutorizadores) {
      loadUsuarios()
    }
    if (canViewAutorizadores) {
      loadAutorizadores()
    }
  }, [
    permissionsSignature,
    canViewProcessos,
    canViewSetores,
    canViewEquipes,
    canViewAutorizadores,
    canCreateProcessos,
    canEditProcessos,
    canManageUsers,
    canCreateSetores,
    canCreateEquipes,
    loadProcessos,
    loadSetores,
    loadMeusSetores,
    loadUsuarios,
    loadAutorizadores,
  ])

  // Função helper para mudar o modo
  const changeMode = (mode: ViewMode) => {
    if (externalCurrentMode === undefined) {
      setInternalCurrentMode(mode)
    }
  }

  // Ajustar tipo atual baseado nas permissões
  useEffect(() => {
    if (!permissions.canViewProcessos && permissions.canViewSetores && externalCurrentType === undefined) {
      setInternalCurrentType('setores')
    } else if (!permissions.canViewProcessos && permissions.canViewEquipes && externalCurrentType === undefined) {
      setInternalCurrentType('equipes')
    }
  }, [permissions, externalCurrentType])

  // Funções CRUD para Processos usando TanStack Query mutations
  const createProcesso = async (data: FormData | Record<string, unknown>) => {
    try {
      await createProcessoMutation.mutateAsync(data as FormData)
      changeMode('list')
      // Toast é tratado pela mutation
    } catch (error) {
      console.error('Erro ao criar processo:', error)
      throw error
    }
  }

  const updateProcesso = async (id: number, data: FormData | Record<string, unknown>) => {
    try {
      await updateProcessoMutation.mutateAsync({ id, data: data as FormData })
      changeMode('list')
      setEditingItem(null)
      // Toast é tratado pela mutation
    } catch (error) {
      console.error('Erro ao atualizar processo:', error)
      throw error
    }
  }

  const deleteProcesso = async (processo: Processo) => {
    try {
      await deleteProcessoMutation.mutateAsync(processo)
      // Toast é tratado pela mutation
    } catch (error) {
      console.error('Erro ao excluir processo:', error)
      throw error
    }
  }

  // Funções CRUD para Setores
  const createSetor = async (data: Record<string, unknown>) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await createSetorMutation.mutateAsync(data as any)
      // Toast é tratado pela mutation
      changeMode('list')
    } catch (error) {
      console.error('Erro ao criar setor:', error)
      throw error
    }
  }

  const updateSetor = async (id: number, data: Record<string, unknown>) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await updateSetorMutation.mutateAsync({ id, data: data as any })
      // Toast é tratado pela mutation
      changeMode('list')
      setEditingItem(null)
    } catch (error) {
      console.error('Erro ao atualizar setor:', error)
      throw error
    }
  }

  const deleteSetor = async (setor: Setor) => {
    try {
      await deleteSetorMutation.mutateAsync(setor)
      // Toast é tratado pela mutation
    } catch (error) {
      console.error('Erro ao excluir setor:', error)
      throw error
    }
  }

  // Funções CRUD para Equipes
  const createEquipe = async (data: Record<string, unknown>) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await createEquipeMutation.mutateAsync(data as any)
      // Toast é tratado pela mutation
      changeMode('list')
    } catch (error) {
      console.error('Erro ao criar equipe:', error)
      throw error
    }
  }

  // Funções CRUD para Autorizadores - TEMPORÁRIO: Desabilitadas
  const createAutorizador = async (_data: Record<string, unknown>) => {
    // TEMPORÁRIO: Desabilitado pois API foi removida
    toast.error('Funcionalidade de autorizadores temporariamente indisponível')
    throw new Error('Autorizadores desabilitados')
  }

  const updateAutorizador = async (_id: number, _data: Record<string, unknown>) => {
    // TEMPORÁRIO: Desabilitado pois API foi removida
    toast.error('Funcionalidade de autorizadores temporariamente indisponível')
    throw new Error('Autorizadores desabilitados')
  }

  const deleteAutorizador = async (_autorizador: Autorizador) => {
    // TEMPORÁRIO: Desabilitado pois API foi removida
    toast.error('Funcionalidade de autorizadores temporariamente indisponível')
    throw new Error('Autorizadores desabilitados')
  }

  // Funções CRUD para Sistemas
  const sanitizeSistemaPayload = (data: CreateSistemaRequest): Omit<CreateSistemaRequest, 'modulos' | 'perfis'> => {
    const { modulos: _modulos, perfis: _perfis, ...payload } = data
    return payload
  }

  const normalizeSistemaItems = (items?: SistemaItemInput[]): SistemaItemInput[] => {
    return (items ?? [])
      .filter((item) => item.nome?.trim().length > 0)
      .map((item) => ({
        ...(item.id ? { id: item.id } : {}),
        nome: item.nome.trim(),
        ...(item.descricao ? { descricao: item.descricao.trim() } : {}),
      }))
  }

  const syncModulos = async (sistemaId: number, previousItems: SistemaItemInput[], nextItems: SistemaItemInput[]) => {
    const previousIds = new Set(previousItems.filter((item) => item.id).map((item) => item.id as number))
    const nextIds = new Set(nextItems.filter((item) => item.id).map((item) => item.id as number))

    const deletedIds = [...previousIds].filter((id) => !nextIds.has(id))
    for (const moduloId of deletedIds) {
      await sistemasService.deleteModulo(moduloId)
    }

    for (const modulo of nextItems) {
      if (modulo.id) {
        await sistemasService.updateModulo(modulo.id, {
          nome: modulo.nome,
          ...(modulo.descricao ? { descricao: modulo.descricao } : {}),
        })
        continue
      }

      await sistemasService.createModulo({
        nome: modulo.nome,
        ...(modulo.descricao ? { descricao: modulo.descricao } : {}),
        sistema_fk: sistemaId,
      })
    }
  }

  const syncPerfis = async (sistemaId: number, previousItems: SistemaItemInput[], nextItems: SistemaItemInput[]) => {
    const previousIds = new Set(previousItems.filter((item) => item.id).map((item) => item.id as number))
    const nextIds = new Set(nextItems.filter((item) => item.id).map((item) => item.id as number))

    const deletedIds = [...previousIds].filter((id) => !nextIds.has(id))
    for (const perfilId of deletedIds) {
      await sistemasService.deletePerfil(perfilId)
    }

    for (const perfil of nextItems) {
      if (perfil.id) {
        await sistemasService.updatePerfil(perfil.id, {
          nome: perfil.nome,
          ...(perfil.descricao ? { descricao: perfil.descricao } : {}),
        })
        continue
      }

      await sistemasService.createPerfil({
        nome: perfil.nome,
        ...(perfil.descricao ? { descricao: perfil.descricao } : {}),
        sistema_fk: sistemaId,
      })
    }
  }

  const createSistema = async (data: import('../../../models').CreateSistemaRequest | FormData) => {
    try {
      const sistemaData = data as CreateSistemaRequest
      const modulos = normalizeSistemaItems((sistemaData.modulos as SistemaItemInput[] | undefined) ?? [])
      const perfis = normalizeSistemaItems((sistemaData.perfis as SistemaItemInput[] | undefined) ?? [])

      const sistema = await createSistemaMutation.mutateAsync(sanitizeSistemaPayload(sistemaData))

      await syncModulos(sistema.id, [], modulos)
      await syncPerfis(sistema.id, [], perfis)

      await refetchSistemas()
      changeMode('list')
    } catch (error) {
      console.error('Erro ao criar sistema:', error)
      throw error
    }
  }

  const updateSistema = async (id: number, data: import('../../../models').CreateSistemaRequest | FormData) => {
    try {
      const sistemaData = data as CreateSistemaRequest
      const currentSistema = sistemasState.find((sistema) => sistema.id === id)

      const previousModulos = normalizeSistemaItems(
        ((currentSistema?.modulos ?? []) as SistemaItemInput[])
      )
      const previousPerfis = normalizeSistemaItems(
        ((currentSistema?.perfis ?? []) as SistemaItemInput[])
      )

      const nextModulos = normalizeSistemaItems((sistemaData.modulos as SistemaItemInput[] | undefined) ?? [])
      const nextPerfis = normalizeSistemaItems((sistemaData.perfis as SistemaItemInput[] | undefined) ?? [])

      await updateSistemaMutation.mutateAsync({ id, data: sanitizeSistemaPayload(sistemaData) })

      await syncModulos(id, previousModulos, nextModulos)
      await syncPerfis(id, previousPerfis, nextPerfis)

      await refetchSistemas()
      changeMode('list')
      setEditingItem(null)
    } catch (error) {
      console.error('Erro ao atualizar sistema:', error)
      throw error
    }
  }

  const deleteSistema = async (sistema: Sistema) => {
    try {
      await deleteSistemaMutation.mutateAsync(sistema.id)
    } catch (error) {
      console.error('Erro ao excluir sistema:', error)
      throw error
    }
  }

  const handleEditSistema = (sistema: Sistema) => {
    setEditingItem(sistema)
  }

  // Funções de gerenciamento de usuários
  const vincularUsuariosSetor = async (setorId: number, usuariosIds: number[]) => {
    try {
      await apiService.vincularUsuariosSetor(setorId, usuariosIds)
    } catch (error) {
      console.error('Erro ao vincular usuários:', error)
      throw error
    }
  }

  const desvincularUsuarioSetor = async (usuarioId: number, setorId: number) => {
    try {
      await apiService.desvincularUsuarioSetor(usuarioId, setorId)
    } catch (error) {
      console.error('Erro ao desvincular usuário:', error)
      throw error
    }
  }

  // Handlers para a navegação
  const handleTypeChange = (type: CrudType) => {
    // Só atualiza estado interno se não houver controle externo
    if (externalCurrentType === undefined) {
      setInternalCurrentType(type)
    }
    if (externalCurrentMode === undefined) {
      setInternalCurrentMode('list')
    }
    setEditingItem(null)
  }

  const handleModeChange = (mode: ViewMode) => {
    // Só atualiza estado interno se não houver controle externo
    if (externalCurrentMode === undefined) {
      setInternalCurrentMode(mode)
    }
    if (mode === 'list') {
      setEditingItem(null)
    }
  }

  // Handlers para ações dos itens
  const handleEditProcesso = (processo: Processo) => {
    setEditingItem(processo)
    changeMode('edit')
  }

  const handleEditSetor = (setor: Setor) => {
    setEditingItem(setor)
    changeMode('edit')
  }

  const handleEditAutorizador = (autorizador: Autorizador) => {
    setEditingItem(autorizador)
    changeMode('edit')
  }

  const handleManageUsers = (setor: Setor) => {
    setManageUsersModal({ isOpen: true, setor })
  }

  const handleCloseManageUsers = () => {
    setManageUsersModal({ isOpen: false, setor: null })
  }

  const refreshSetorData = useCallback(async () => {
    if (!manageUsersModal.setor) {
      return
    }

    try {
      const updatedSetor = await apiService.getSetorComUsuarios(manageUsersModal.setor.id)
      setManageUsersModal(prev => ({ ...prev, setor: updatedSetor }))
      await loadSetores() // Atualizar lista principal também
    } catch (error) {
      console.error('Erro ao atualizar dados do setor:', error)
    }
  }, [manageUsersModal.setor, loadSetores])

  return {
    // Dados
    usuarios,
    setores,
    todosSetores, // Todos os setores + equipes (para uso no formulário de processos)
    processos,
    meusSetores,
    autorizadores,
    sistemas: sistemasState,
    
    // Estados de carregamento
    isLoadingUsuarios,
    isLoadingSetores,
    isLoadingTodosSetores: isLoadingTodosSetoresState,
    isLoadingProcessos,
    isLoadingMeusSetores,
    isLoadingAutorizadores,
    isLoadingSistemas,
    
    // Estados da navegação
    currentType,
    currentMode,
    editingItem,
    setEditingItem,
    
    // Modal de gerenciamento
    manageUsersModal,
    
    // Permissões
    permissions,
    
    // Ações de navegação
    handleTypeChange,
    handleModeChange,
    
    // Ações CRUD Processos
    createProcesso,
    updateProcesso,
    deleteProcesso,
    handleEditProcesso,
    
    // Ações CRUD Setores
    createSetor,
    updateSetor,
    deleteSetor,
    handleEditSetor,
    
    // Ações CRUD Equipes
    createEquipe,
    
    // Ações CRUD Autorizadores
    createAutorizador,
    updateAutorizador,
    deleteAutorizador,
    handleEditAutorizador,
    
    // Ações CRUD Sistemas
    createSistema,
    updateSistema,
    deleteSistema,
    handleEditSistema,
    
    // Ações de gerenciamento de usuários
    handleManageUsers,
    handleCloseManageUsers,
    vincularUsuariosSetor,
    desvincularUsuarioSetor,
    refreshSetorData,
    
    // Funções de reload
    loadProcessos,
    loadSetores,
    loadUsuarios,
    loadMeusSetores,
    loadAutorizadores,
  }
}
